import { Box, Modal, Divider, ImageList, ImageListItem } from "@mui/material";
import ButtonComponent from "../Inputs/ButtonComponent";
import "../../CSS/ComponentCSS/FaceSizeModal.css";
import TextComponent from "../Inputs/TextComponent";
import { Typography } from "antd";
import { ArrowForwardIos } from "@mui/icons-material";
import { useEffect, useMemo, useState } from "react";
import AlertComponent from "../Alert/AlertComponent";
import Loading from "../LoadingBar/Loading";
import Api from "../../API/api";
import {
  Stage,
  Layer,
  Image as KonvaImage,
  Line,
  Circle,
  Text,
} from "react-konva";
import useImage from "use-image";
import { filesList } from "../CommonFunction";

type Point = { x: number; y: number } | null;

function getDistancePx(p1: Point, p2: Point): number {
  if (!p1 || !p2) return 0; // 항상 number 반환
  const dx = p2.x - p1.x;
  const dy = p2.y - p1.y;
  return Math.round(Math.hypot(dx, dy));
}

function includedAngleDeg(p1: Point, p2: Point, p3: Point, p4: Point): number {
  if (!p1 || !p2 || !p3 || !p4) return 0;

  const v1x = p2.x - p1.x,
    v1y = p2.y - p1.y;
  const v2x = p4.x - p3.x,
    v2y = p4.y - p3.y;
  const n1 = Math.hypot(v1x, v1y),
    n2 = Math.hypot(v2x, v2y);
  if (n1 < 1e-6 || n2 < 1e-6) return 0;

  // cos θ = (v1·v2)/(|v1||v2|)
  let cos = (v1x * v2x + v1y * v2y) / (n1 * n2);
  cos = Math.min(1, Math.max(-1, cos)); // 수치 안정화
  const theta = Math.acos(cos); // 0~π
  return deg(theta); // 0~180
}

function deg(rad: number) {
  return parseFloat(((rad * 180) / Math.PI).toFixed(2)) > 90
    ? 180 - parseFloat(((rad * 180) / Math.PI).toFixed(2))
    : parseFloat(((rad * 180) / Math.PI).toFixed(2));
}

type FaceSizeModalProps = {
  open: boolean;
  onClose: (value: any) => void;
  state: string;
  files: any;
  p_id: number;
  cancel?: boolean;
};

const { Title } = Typography;

export default function FaceSizeModal(props: FaceSizeModalProps) {
  const [selectFiles, setSelectFiles] = useState<any>([]);
  const [selectSideFiles, setSelectSideFiles] = useState<any>([]);
  const [faceData, setFaceData] = useState({
    clown: 0,
    face: 0,
    forehead: 0,
    chin: 0,
    chin_col: 0,
    edge: 0,
  });
  const [imgSize, setImgSize] = useState({ width: 0, height: 0 });
  const [imgSideSize, setImgSideSize] = useState({ width: 0, height: 0 });
  const [p1, setP1] = useState<any>(null);
  const [p2, setP2] = useState<any>(null);
  const [p3, setP3] = useState<any>(null);
  const [p4, setP4] = useState<any>(null);
  const [image] = useImage(selectFiles ? `${filesList(selectFiles)}` : "");
  const [sideimage] = useImage(
    selectSideFiles ? `${filesList(selectSideFiles)}` : ""
  );
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(0);
  const [faceResult, setFaceResult] = useState({
    clown: 0,
    face: 0,
    forehead: 0,
    chin: 0,
    chin_col: 0,
    edge: 0,
  });

  useEffect(() => {
    if (props.p_id != 0) {
      const getResult = async () => {
        setLoading(true);
        const Face = await Api.get(`/Consult/getFaceResult?p_id=${props.p_id}`);
        setLoading(false);
        if (Face != undefined) {
          setFaceResult(Face[0]);
        }
      };

      getResult();
    }
  }, []);

  // 이미지가 로드되면 Stage 크기를 이미지 크기에 맞춤 (화면 px = Stage px)
  const handleImageRef = (node: any) => {
    if (node && image && (imgSize.width === 0 || imgSize.height === 0)) {
      setImgSize({
        width: image.width,
        height: image.height,
      });
    }
  };
  const handleSideImageRef = (node: any) => {
    if (
      node &&
      sideimage &&
      (imgSideSize.width === 0 || imgSideSize.height === 0)
    ) {
      setImgSideSize({
        width: sideimage.width,
        height: sideimage.height,
      });
    }
  };

  const STAGE_W = 500;
  const STAGE_H = 600;

  const fitted = useMemo(() => {
    if (!image) return { w: 0, h: 0, x: 0, y: 0, scale: 1 };
    const iw = image.width;
    const ih = image.height;
    const s = Math.min(STAGE_W / iw, STAGE_H / ih); // 동일 배율
    const w = iw * s;
    const h = ih * s;
    const x = (STAGE_W - w) / 2; // 가운데 정렬
    const y = (STAGE_H - h) / 2;
    return { w, h, x, y, scale: s };
  }, [image]);

  const fitted2 = useMemo(() => {
    if (!sideimage) return { w: 0, h: 0, x: 0, y: 0, scale: 1 };
    const iw = sideimage.width;
    const ih = sideimage.height;
    const s = Math.min(STAGE_W / iw, STAGE_H / ih); // 동일 배율
    const w = iw * s;
    const h = ih * s;
    const x = (STAGE_W - w) / 2; // 가운데 정렬
    const y = (STAGE_H - h) / 2;
    return { w, h, x, y, scale: s };
  }, [sideimage]);

  const handleClick = (e: { target: { getStage: () => any } }) => {
    const stage = e.target.getStage();
    const pos = stage.getPointerPosition();
    if (!p1) {
      setP1(pos);
      setP2(null);
    } else if (!p2) {
      setP2(pos);

      if (step == 0) {
        setFaceData((prev) => ({
          ...prev,
          clown: getDistancePx(p1, pos),
        }));
      } else if (step == 1) {
        setFaceData((prev) => ({
          ...prev,
          face: getDistancePx(p1, pos),
        }));
      } else if (step == 2) {
        setFaceData((prev) => ({
          ...prev,
          forehead: getDistancePx(p1, pos),
        }));
      } else if (step == 3) {
        setFaceData((prev) => ({
          ...prev,
          chin: getDistancePx(p1, pos),
        }));
      } else if (step == 4) {
        setFaceData((prev) => ({
          ...prev,
          chin_col: getDistancePx(p1, pos),
        }));
      }
    } else {
      // 세 번째 클릭부터는 다시 시작점으로
      setP1(pos);
      setP2(null);
    }
  };

  const handleSideClick = (e: { target: { getStage: () => any } }) => {
    const stage = e.target.getStage();
    const pos = stage.getPointerPosition();
    if (!p1) {
      setP1(pos);
      setP2(null);
    } else if (!p2) {
      setP2(pos);
    } else if (!p3) {
      setP3(pos);
    } else if (!p4) {
      setP4(pos);
      setFaceData((prev) => ({
        ...prev,
        edge: includedAngleDeg(p1, p2, p3, pos),
      }));
    } else {
      setP1(pos);
      setP2(null);
      setP3(null);
      setP4(null);
    }
  };

  const changeTextField = (value: any) => {
    if (value.name == "clown") {
      setFaceData((prev: any) => ({
        ...prev,
        clown: value.value,
      }));
    } else if (value.name == "face") {
      setFaceData((prev: any) => ({
        ...prev,
        face: value.value,
      }));
    } else if (value.name == "forehead") {
      setFaceData((prev: any) => ({
        ...prev,
        forehead: value.value,
      }));
    } else if (value.name == "chin") {
      setFaceData((prev: any) => ({
        ...prev,
        chin: value.value,
      }));
    } else if (value.name == "chin_col") {
      setFaceData((prev: any) => ({
        ...prev,
        chin_col: value.value,
      }));
    } else if (value.name == "edge") {
      setFaceData((prev: any) => ({
        ...prev,
        edge: value.value,
      }));
    }
  };

  const handlecalculate = () => {
    setFaceResult({
      clown: 1,
      face: Number(((faceData.face / faceData.clown) * 2).toFixed(1)),
      forehead: Number(((faceData.forehead / faceData.clown) * 2).toFixed(1)),
      chin: Number(((faceData.chin / faceData.clown) * 2).toFixed(1)),
      chin_col: Number(((faceData.chin_col / faceData.clown) * 2).toFixed(1)),
      edge: faceData.edge,
    });

    props.onClose({
      clown: 1,
      face: Number(((faceData.face / faceData.clown) * 2).toFixed(1)),
      forehead: Number(((faceData.forehead / faceData.clown) * 2).toFixed(1)),
      chin: Number(((faceData.chin / faceData.clown) * 2).toFixed(1)),
      chin_col: Number(((faceData.chin_col / faceData.clown) * 2).toFixed(1)),
      edge: faceData.edge,
    });
  };

  return (
    <Modal open={props.open} onClose={props.onClose}>
      <div>
        {props.state == "view" ? (
          <Box className="FaceSizeModalBox2">
            <Box className="ConsultDetailForm">
              <Title level={4} className="ConsultDetailName">
                광대비율
              </Title>
              <Title level={5} className="ConsultDetailName">
                <ArrowForwardIos
                  fontSize="small"
                  className="ConsultAnswerIcon"
                />
                {faceResult.clown}
              </Title>
            </Box>
            <Box className="ConsultDetailForm">
              <Title level={4} className="ConsultDetailName">
                얼굴비율
              </Title>
              <Title level={5} className="ConsultDetailName">
                <ArrowForwardIos
                  fontSize="small"
                  className="ConsultAnswerIcon"
                />
                {faceResult.face}
              </Title>
            </Box>
            <Box className="ConsultDetailForm">
              <Title level={4} className="ConsultDetailName">
                이마비율
              </Title>
              <Title level={5} className="ConsultDetailName">
                <ArrowForwardIos
                  fontSize="small"
                  className="ConsultAnswerIcon"
                />
                {faceResult.forehead}
              </Title>
            </Box>
            <Box className="ConsultDetailForm">
              <Title level={4} className="ConsultDetailName">
                턱비율(가로)
              </Title>
              <Title level={5} className="ConsultDetailName">
                <ArrowForwardIos
                  fontSize="small"
                  className="ConsultAnswerIcon"
                />
                {faceResult.chin}
              </Title>
            </Box>
            <Box className="ConsultDetailForm">
              <Title level={4} className="ConsultDetailName">
                턱비율(세로)
              </Title>
              <Title level={5} className="ConsultDetailName">
                <ArrowForwardIos
                  fontSize="small"
                  className="ConsultAnswerIcon"
                />
                {faceResult.chin_col}
              </Title>
            </Box>
            <Box className="ConsultDetailForm">
              <Title level={4} className="ConsultDetailName">
                턱각도
              </Title>
              <Title level={5} className="ConsultDetailName">
                <ArrowForwardIos
                  fontSize="small"
                  className="ConsultAnswerIcon"
                />
                {faceResult.edge}
              </Title>
            </Box>
            <div className="FaceSizeModalBottom">
              {props.cancel && (
                <ButtonComponent
                  fullWidth
                  variant="contained"
                  onClick={() => props.onClose(false)}
                  className="BodySizeModalButton"
                >
                  취소
                </ButtonComponent>
              )}
            </div>
          </Box>
        ) : (
          <Box className="FaceSizeModalBox">
            {selectFiles.length == 0 ? (
              <>
                <Box className="ConsultDetailForm">
                  <Title level={4} className="ConsultDetailName">
                    정면 사진 선택(클릭해주세요)
                  </Title>
                  <ImageList sx={{ width: "100%" }} cols={1} gap={2}>
                    {props.files
                      ? props.files.map((item: any, index: any) => (
                          <ImageListItem key={index}>
                            <img
                              srcSet={`${filesList(
                                item
                              )}?w=164&h=164&fit=crop&auto=format&dpr=2 2x`}
                              src={`${filesList(item)}?auto=format`}
                              loading="lazy"
                              style={{
                                width: "100%",
                                height: "300px",
                                objectFit: "contain",
                                cursor: "pointer",
                              }}
                              onClick={() => setSelectFiles(item)}
                            />
                          </ImageListItem>
                        ))
                      : ""}
                  </ImageList>
                </Box>
                <div className="FaceSizeModalBottom">
                  {props.cancel && (
                    <ButtonComponent
                      fullWidth
                      variant="contained"
                      onClick={() => props.onClose(false)}
                      className="BodySizeModalButton"
                    >
                      취소
                    </ButtonComponent>
                  )}
                </div>
              </>
            ) : step == 0 ? (
              <>
                <div style={{ maxWidth: "100%", overflow: "auto" }}>
                  <div style={{ marginBottom: 8, display: "flex", gap: 8 }}>
                    <Title level={5} className="ConsultDetailName">
                      광대길이 측정(이미지 위를 두 번 클릭해 측정하세요)
                    </Title>
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <div
                      style={{
                        display: "inline-block",
                        cursor: "crosshair",
                      }}
                    >
                      <Stage
                        width={STAGE_W}
                        height={STAGE_H}
                        onClick={handleClick}
                      >
                        <Layer>
                          {/* 이미지 */}
                          <KonvaImage
                            x={fitted.x}
                            y={fitted.y}
                            width={fitted.w}
                            height={fitted.h}
                            image={image}
                            ref={handleImageRef}
                            listening={false}
                          />

                          {/* 선 (두 점이 모두 있을 때) */}
                          {p1 && p2 && (
                            <Line
                              points={[p1.x, p1.y, p2.x, p2.y]}
                              stroke="lime"
                              strokeWidth={2}
                              listening={false}
                            />
                          )}

                          {/* 점 마커 */}
                          {p1 && (
                            <Circle
                              x={p1.x}
                              y={p1.y}
                              radius={5}
                              fill="red"
                              listening={false}
                            />
                          )}
                          {p2 && (
                            <Circle
                              x={p2.x}
                              y={p2.y}
                              radius={5}
                              fill="blue"
                              listening={false}
                            />
                          )}
                        </Layer>
                      </Stage>
                    </div>

                    <div>
                      <img
                        src={`/fx_cheek.png`}
                        loading="lazy"
                        style={{
                          width: "100%",
                          height: "300px",
                          objectFit: "contain",
                        }}
                      />
                      <Title level={4} className="ConsultDetailName">
                        광대길이
                      </Title>
                      {props.state == "view" ? (
                        <Title level={5} className="ConsultDetailName">
                          <ArrowForwardIos
                            fontSize="small"
                            className="ConsultAnswerIcon"
                          />
                        </Title>
                      ) : (
                        <Box>
                          <TextComponent
                            variant="standard"
                            value={faceData.clown}
                            name="clown"
                            fullWidth
                            onChange={changeTextField}
                            type="number"
                            endAdornment={
                              <Title level={5} className="ConsultDetailName">
                                px
                              </Title>
                            }
                          />
                        </Box>
                      )}
                    </div>
                  </div>
                </div>
                <div className="FaceSizeModalBottom">
                  <ButtonComponent
                    fullWidth
                    variant="contained"
                    onClick={() => {
                      setP1(null);
                      setP2(null);
                    }}
                    className="BodySizeModalButton"
                  >
                    Reset
                  </ButtonComponent>
                  <ButtonComponent
                    fullWidth
                    variant="contained"
                    onClick={() => {
                      if (faceData.clown == 0) {
                        return;
                      }
                      setP1(null);
                      setP2(null);
                      setStep(1);
                    }}
                    className="BodySizeModalButton"
                  >
                    Next
                  </ButtonComponent>
                  {props.cancel && (
                    <ButtonComponent
                      fullWidth
                      variant="contained"
                      onClick={() => props.onClose(false)}
                      className="BodySizeModalButton"
                    >
                      취소
                    </ButtonComponent>
                  )}
                </div>
              </>
            ) : step == 1 ? (
              <>
                <div style={{ maxWidth: "100%", overflow: "auto" }}>
                  <div style={{ marginBottom: 8, display: "flex", gap: 8 }}>
                    <Title level={5} className="ConsultDetailName">
                      얼굴길이 측정(이미지 위를 두 번 클릭해 측정하세요)
                    </Title>
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <div
                      style={{
                        display: "inline-block",
                        cursor: "crosshair",
                      }}
                    >
                      <Stage
                        width={STAGE_W}
                        height={STAGE_H}
                        onClick={handleClick}
                      >
                        <Layer>
                          {/* 이미지 */}
                          <KonvaImage
                            x={fitted.x}
                            y={fitted.y}
                            width={fitted.w}
                            height={fitted.h}
                            image={image}
                            ref={handleImageRef}
                            listening={false}
                          />

                          {/* 선 (두 점이 모두 있을 때) */}
                          {p1 && p2 && (
                            <Line
                              points={[p1.x, p1.y, p2.x, p2.y]}
                              stroke="lime"
                              strokeWidth={2}
                              listening={false}
                            />
                          )}

                          {/* 점 마커 */}
                          {p1 && (
                            <Circle
                              x={p1.x}
                              y={p1.y}
                              radius={5}
                              fill="red"
                              listening={false}
                            />
                          )}
                          {p2 && (
                            <Circle
                              x={p2.x}
                              y={p2.y}
                              radius={5}
                              fill="blue"
                              listening={false}
                            />
                          )}
                        </Layer>
                      </Stage>
                    </div>

                    <div>
                      <img
                        src={`/fx_face.png`}
                        loading="lazy"
                        style={{
                          width: "100%",
                          height: "300px",
                          objectFit: "contain",
                        }}
                      />
                      <Title level={4} className="ConsultDetailName">
                        얼굴길이
                      </Title>
                      {props.state == "view" ? (
                        <Title level={5} className="ConsultDetailName">
                          <ArrowForwardIos
                            fontSize="small"
                            className="ConsultAnswerIcon"
                          />
                        </Title>
                      ) : (
                        <Box>
                          <TextComponent
                            variant="standard"
                            value={faceData.face}
                            name="face"
                            fullWidth
                            onChange={changeTextField}
                            type="number"
                            endAdornment={
                              <Title level={5} className="ConsultDetailName">
                                px
                              </Title>
                            }
                          />
                        </Box>
                      )}
                    </div>
                  </div>
                </div>
                <div className="FaceSizeModalBottom">
                  <ButtonComponent
                    fullWidth
                    variant="contained"
                    onClick={() => {
                      setP1(null);
                      setP2(null);
                    }}
                    className="BodySizeModalButton"
                  >
                    Reset
                  </ButtonComponent>
                  <ButtonComponent
                    fullWidth
                    variant="contained"
                    onClick={() => {
                      if (faceData.face == 0) {
                        return;
                      }
                      setP1(null);
                      setP2(null);
                      setStep(2);
                    }}
                    className="BodySizeModalButton"
                  >
                    Next
                  </ButtonComponent>
                  {props.cancel && (
                    <ButtonComponent
                      fullWidth
                      variant="contained"
                      onClick={() => props.onClose(false)}
                      className="BodySizeModalButton"
                    >
                      취소
                    </ButtonComponent>
                  )}
                </div>
              </>
            ) : step == 2 ? (
              <>
                <div style={{ maxWidth: "100%", overflow: "auto" }}>
                  <div style={{ marginBottom: 8, display: "flex", gap: 8 }}>
                    <Title level={5} className="ConsultDetailName">
                      이마길이 측정(이미지 위를 두 번 클릭해 측정하세요)
                    </Title>
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <div
                      style={{
                        display: "inline-block",
                        cursor: "crosshair",
                      }}
                    >
                      <Stage
                        width={STAGE_W}
                        height={STAGE_H}
                        onClick={handleClick}
                      >
                        <Layer>
                          {/* 이미지 */}
                          <KonvaImage
                            x={fitted.x}
                            y={fitted.y}
                            width={fitted.w}
                            height={fitted.h}
                            image={image}
                            ref={handleImageRef}
                            listening={false}
                          />

                          {/* 선 (두 점이 모두 있을 때) */}
                          {p1 && p2 && (
                            <Line
                              points={[p1.x, p1.y, p2.x, p2.y]}
                              stroke="lime"
                              strokeWidth={2}
                              listening={false}
                            />
                          )}

                          {/* 점 마커 */}
                          {p1 && (
                            <Circle
                              x={p1.x}
                              y={p1.y}
                              radius={5}
                              fill="red"
                              listening={false}
                            />
                          )}
                          {p2 && (
                            <Circle
                              x={p2.x}
                              y={p2.y}
                              radius={5}
                              fill="blue"
                              listening={false}
                            />
                          )}
                        </Layer>
                      </Stage>
                    </div>
                    <div>
                      <img
                        src={`/fx_forehead.png`}
                        loading="lazy"
                        style={{
                          width: "100%",
                          height: "300px",
                          objectFit: "contain",
                        }}
                      />
                      <Title level={4} className="ConsultDetailName">
                        이마길이
                      </Title>
                      {props.state == "view" ? (
                        <Title level={5} className="ConsultDetailName">
                          <ArrowForwardIos
                            fontSize="small"
                            className="ConsultAnswerIcon"
                          />
                        </Title>
                      ) : (
                        <Box>
                          <TextComponent
                            variant="standard"
                            value={faceData.forehead}
                            name="forehead"
                            fullWidth
                            onChange={changeTextField}
                            type="number"
                            endAdornment={
                              <Title level={5} className="ConsultDetailName">
                                px
                              </Title>
                            }
                          />
                        </Box>
                      )}
                    </div>
                  </div>
                </div>
                <div className="FaceSizeModalBottom">
                  <ButtonComponent
                    fullWidth
                    variant="contained"
                    onClick={() => {
                      setP1(null);
                      setP2(null);
                    }}
                    className="BodySizeModalButton"
                  >
                    Reset
                  </ButtonComponent>
                  <ButtonComponent
                    fullWidth
                    variant="contained"
                    onClick={() => {
                      if (faceData.forehead == 0) {
                        return;
                      }
                      setP1(null);
                      setP2(null);
                      setStep(3);
                    }}
                    className="BodySizeModalButton"
                  >
                    Next
                  </ButtonComponent>
                  {props.cancel && (
                    <ButtonComponent
                      fullWidth
                      variant="contained"
                      onClick={() => props.onClose(false)}
                      className="BodySizeModalButton"
                    >
                      취소
                    </ButtonComponent>
                  )}
                </div>
              </>
            ) : step == 3 ? (
              <>
                <div style={{ maxWidth: "100%", overflow: "auto" }}>
                  <div style={{ marginBottom: 8, display: "flex", gap: 8 }}>
                    <Title level={5} className="ConsultDetailName">
                      턱길이 측정(이미지 위를 두 번 클릭해 측정하세요)
                    </Title>
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <div
                      style={{
                        display: "inline-block",
                        cursor: "crosshair",
                      }}
                    >
                      <Stage
                        width={STAGE_W}
                        height={STAGE_H}
                        onClick={handleClick}
                      >
                        <Layer>
                          {/* 이미지 */}
                          <KonvaImage
                            x={fitted.x}
                            y={fitted.y}
                            width={fitted.w}
                            height={fitted.h}
                            image={image}
                            ref={handleImageRef}
                            listening={false}
                          />

                          {/* 선 (두 점이 모두 있을 때) */}
                          {p1 && p2 && (
                            <Line
                              points={[p1.x, p1.y, p2.x, p2.y]}
                              stroke="lime"
                              strokeWidth={2}
                              listening={false}
                            />
                          )}

                          {/* 점 마커 */}
                          {p1 && (
                            <Circle
                              x={p1.x}
                              y={p1.y}
                              radius={5}
                              fill="red"
                              listening={false}
                            />
                          )}
                          {p2 && (
                            <Circle
                              x={p2.x}
                              y={p2.y}
                              radius={5}
                              fill="blue"
                              listening={false}
                            />
                          )}
                        </Layer>
                      </Stage>
                    </div>

                    <div>
                      <img
                        src={`/fx_jaw.png`}
                        loading="lazy"
                        style={{
                          width: "100%",
                          height: "300px",
                          objectFit: "contain",
                        }}
                      />
                      <Title level={4} className="ConsultDetailName">
                        턱길이
                      </Title>
                      {props.state == "view" ? (
                        <Title level={5} className="ConsultDetailName">
                          <ArrowForwardIos
                            fontSize="small"
                            className="ConsultAnswerIcon"
                          />
                        </Title>
                      ) : (
                        <Box>
                          <TextComponent
                            variant="standard"
                            value={faceData.chin}
                            name="chin"
                            fullWidth
                            onChange={changeTextField}
                            type="number"
                            endAdornment={
                              <Title level={5} className="ConsultDetailName">
                                px
                              </Title>
                            }
                          />
                        </Box>
                      )}
                    </div>
                  </div>
                </div>
                <div className="FaceSizeModalBottom">
                  <ButtonComponent
                    fullWidth
                    variant="contained"
                    onClick={() => {
                      setP1(null);
                      setP2(null);
                    }}
                    className="BodySizeModalButton"
                  >
                    Reset
                  </ButtonComponent>
                  <ButtonComponent
                    fullWidth
                    variant="contained"
                    onClick={() => {
                      if (faceData.chin == 0) {
                        return;
                      }
                      setP1(null);
                      setP2(null);
                      setStep(4);
                    }}
                    className="BodySizeModalButton"
                  >
                    Next
                  </ButtonComponent>
                  {props.cancel && (
                    <ButtonComponent
                      fullWidth
                      variant="contained"
                      onClick={() => props.onClose(false)}
                      className="BodySizeModalButton"
                    >
                      취소
                    </ButtonComponent>
                  )}
                </div>
              </>
            ) : step == 4 ? (
              <>
                <div style={{ maxWidth: "100%", overflow: "auto" }}>
                  <div style={{ marginBottom: 8, display: "flex", gap: 8 }}>
                    <Title level={5} className="ConsultDetailName">
                      턱길이(세로) 측정(이미지 위를 두 번 클릭해 측정하세요)
                    </Title>
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <div
                      style={{
                        display: "inline-block",
                        cursor: "crosshair",
                      }}
                    >
                      <Stage
                        width={STAGE_W}
                        height={STAGE_H}
                        onClick={handleClick}
                      >
                        <Layer>
                          {/* 이미지 */}
                          <KonvaImage
                            x={fitted.x}
                            y={fitted.y}
                            width={fitted.w}
                            height={fitted.h}
                            image={image}
                            ref={handleImageRef}
                            listening={false}
                          />

                          {/* 선 (두 점이 모두 있을 때) */}
                          {p1 && p2 && (
                            <Line
                              points={[p1.x, p1.y, p2.x, p2.y]}
                              stroke="lime"
                              strokeWidth={2}
                              listening={false}
                            />
                          )}

                          {/* 점 마커 */}
                          {p1 && (
                            <Circle
                              x={p1.x}
                              y={p1.y}
                              radius={5}
                              fill="red"
                              listening={false}
                            />
                          )}
                          {p2 && (
                            <Circle
                              x={p2.x}
                              y={p2.y}
                              radius={5}
                              fill="blue"
                              listening={false}
                            />
                          )}
                        </Layer>
                      </Stage>
                    </div>

                    <div>
                      <img
                        src={`/fx_jaw_col.png`}
                        loading="lazy"
                        style={{
                          width: "100%",
                          height: "300px",
                          objectFit: "contain",
                        }}
                      />
                      <Title level={4} className="ConsultDetailName">
                        턱길이(세로)
                      </Title>
                      {props.state == "view" ? (
                        <Title level={5} className="ConsultDetailName">
                          <ArrowForwardIos
                            fontSize="small"
                            className="ConsultAnswerIcon"
                          />
                        </Title>
                      ) : (
                        <Box>
                          <TextComponent
                            variant="standard"
                            value={faceData.chin_col}
                            name="chin_col"
                            fullWidth
                            onChange={changeTextField}
                            type="number"
                            endAdornment={
                              <Title level={5} className="ConsultDetailName">
                                px
                              </Title>
                            }
                          />
                        </Box>
                      )}
                    </div>
                  </div>
                </div>
                <div className="FaceSizeModalBottom">
                  <ButtonComponent
                    fullWidth
                    variant="contained"
                    onClick={() => {
                      setP1(null);
                      setP2(null);
                    }}
                    className="BodySizeModalButton"
                  >
                    Reset
                  </ButtonComponent>
                  <ButtonComponent
                    fullWidth
                    variant="contained"
                    onClick={() => {
                      if (faceData.chin_col == 0) {
                        return;
                      }
                      setP1(null);
                      setP2(null);
                      setStep(5);
                    }}
                    className="BodySizeModalButton"
                  >
                    Next
                  </ButtonComponent>
                  {props.cancel && (
                    <ButtonComponent
                      fullWidth
                      variant="contained"
                      onClick={() => props.onClose(false)}
                      className="BodySizeModalButton"
                    >
                      취소
                    </ButtonComponent>
                  )}
                </div>
              </>
            ) : step == 5 ? (
              <>
                <Box className="ConsultDetailForm">
                  <Title level={4} className="ConsultDetailName">
                    측면 사진 선택(클릭해주세요)
                  </Title>
                  <ImageList sx={{ width: "100%" }} cols={1} gap={2}>
                    {props.files
                      ? props.files.map((item: any, index: any) => (
                          <ImageListItem key={index}>
                            <img
                              srcSet={`${filesList(
                                item
                              )}?w=164&h=164&fit=crop&auto=format&dpr=2 2x`}
                              src={`${filesList(item)}?auto=format`}
                              loading="lazy"
                              style={{
                                width: "100%",
                                height: "300px",
                                objectFit: "contain",
                                cursor: "pointer",
                              }}
                              onClick={() => {
                                setSelectSideFiles(item);
                                setStep(6);
                              }}
                            />
                          </ImageListItem>
                        ))
                      : ""}
                  </ImageList>
                </Box>
                <div className="FaceSizeModalBottom">
                  {props.cancel && (
                    <ButtonComponent
                      fullWidth
                      variant="contained"
                      onClick={() => props.onClose(false)}
                      className="BodySizeModalButton"
                    >
                      취소
                    </ButtonComponent>
                  )}
                </div>
              </>
            ) : step == 6 ? (
              <>
                <div style={{ maxWidth: "100%", overflow: "auto" }}>
                  <div style={{ marginBottom: 8, display: "flex", gap: 8 }}>
                    <Title level={5} className="ConsultDetailName">
                      턱 각도 측정(이미지 위를 두 번 클릭해 측정하세요)
                    </Title>
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <div
                      style={{
                        display: "inline-block",
                        cursor: "crosshair",
                      }}
                    >
                      <Stage
                        width={STAGE_W}
                        height={STAGE_H}
                        onClick={handleSideClick}
                      >
                        <Layer>
                          {/* 이미지 */}
                          <KonvaImage
                            x={fitted2.x}
                            y={fitted2.y}
                            width={fitted2.w}
                            height={fitted2.h}
                            image={sideimage}
                            ref={handleSideImageRef}
                            listening={false}
                          />

                          {/* 선 (두 점이 모두 있을 때) */}
                          {p1 && p2 && (
                            <Line
                              points={[p1.x, p1.y, p2.x, p2.y]}
                              stroke="lime"
                              strokeWidth={2}
                              listening={false}
                            />
                          )}
                          {p3 && p4 && (
                            <Line
                              points={[p3.x, p3.y, p4.x, p4.y]}
                              stroke="lime"
                              strokeWidth={2}
                              listening={false}
                            />
                          )}
                          {/* 점 마커 */}
                          {p1 && (
                            <Circle
                              x={p1.x}
                              y={p1.y}
                              radius={5}
                              fill="red"
                              listening={false}
                            />
                          )}
                          {p2 && (
                            <Circle
                              x={p2.x}
                              y={p2.y}
                              radius={5}
                              fill="blue"
                              listening={false}
                            />
                          )}
                          {p3 && (
                            <Circle
                              x={p3.x}
                              y={p3.y}
                              radius={5}
                              fill="red"
                              listening={false}
                            />
                          )}
                          {p4 && (
                            <Circle
                              x={p4.x}
                              y={p4.y}
                              radius={5}
                              fill="red"
                              listening={false}
                            />
                          )}
                        </Layer>
                      </Stage>
                    </div>

                    <div>
                      <img
                        src={`/fx_side.png`}
                        loading="lazy"
                        style={{
                          width: "100%",
                          height: "300px",
                          objectFit: "contain",
                        }}
                      />
                      <Title level={4} className="ConsultDetailName">
                        턱각도
                      </Title>
                      {props.state == "view" ? (
                        <Title level={5} className="ConsultDetailName">
                          <ArrowForwardIos
                            fontSize="small"
                            className="ConsultAnswerIcon"
                          />
                        </Title>
                      ) : (
                        <Box>
                          <TextComponent
                            variant="standard"
                            value={faceData.edge}
                            name="edge"
                            fullWidth
                            onChange={changeTextField}
                            type="number"
                            endAdornment={
                              <Title level={5} className="ConsultDetailName">
                                °
                              </Title>
                            }
                          />
                        </Box>
                      )}
                    </div>
                  </div>
                </div>
                <div className="FaceSizeModalBottom">
                  <ButtonComponent
                    fullWidth
                    variant="contained"
                    onClick={() => {
                      setP1(null);
                      setP2(null);
                      setP3(null);
                      setP4(null);
                    }}
                    className="BodySizeModalButton"
                  >
                    Reset
                  </ButtonComponent>
                  <ButtonComponent
                    fullWidth
                    variant="contained"
                    onClick={() => {
                      if (faceData.edge == 0) {
                        return;
                      }
                      setP1(null);
                      setP2(null);
                      setP3(null);
                      setP4(null);

                      handlecalculate();
                    }}
                    className="BodySizeModalButton"
                  >
                    계산하기
                  </ButtonComponent>
                  {props.cancel && (
                    <ButtonComponent
                      fullWidth
                      variant="contained"
                      onClick={() => props.onClose(false)}
                      className="BodySizeModalButton"
                    >
                      취소
                    </ButtonComponent>
                  )}
                </div>
              </>
            ) : (
              ""
            )}
          </Box>
        )}{" "}
        {loading && <Loading />}
      </div>
    </Modal>
  );
}
